import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import Toastify from 'toastify-js'
import moment from 'moment'
import 'moment/dist/locale/fr'
import 'moment/dist/locale/nl-be'
import searchListHelper from '@u/searchListHelper'

dayjs.extend(duration)

const helpers = {
  toastify(text, param = {}) {
    Toastify({
      text: text,
      duration: 3000,
      newWindow: true,
      close: true,
      gravity: 'top',
      position: 'right',
      stopOnFocus: true,
      className: 'toastify-settings',
      ...param
    }).showToast()
  },
  cutText(text, length) {
    if (text.split(' ').length > 1) {
      const string = text.substring(0, length)
      const splitText = string.split(' ')
      splitText.pop()
      return splitText.join(' ') + '...'
    } else {
      return text
    }
  },
  formatDate(date, format, locale) {
    locale = locale !== undefined ? locale : 'fr'
    let _moment = moment(date)
    return _moment.isValid() ? _moment.locale(locale).format(format) : ''
  },
  normalizeSearch(str) {
    return searchListHelper.normalizeSearch(str)
  },
  searchInlist(list, attributes, searchTerms) {
    return searchListHelper.searchInlist(list, attributes, searchTerms)
  },
  /**
   * Rich toast cloned from the #generic_toast template rendered by
   * ToastsContainer (main layout). Falls back to a plain Toastify toast when
   * the template is not mounted (login page).
   */
  toast(type, title, text, uniqId) {
    if (uniqId !== undefined && !!document.getElementById(uniqId)) {
      return
    }
    const template = document.getElementById('generic_toast')
    if (!template) {
      helpers.toastify(text ? `${title} : ${text}` : title, {
        className: `toastify-content ${type}`
      })
      return
    }
    let notificationHtmlElement = template.cloneNode(true)
    notificationHtmlElement.removeAttribute('id')
    if (uniqId !== undefined) {
      notificationHtmlElement.setAttribute('id', uniqId)
    }
    notificationHtmlElement.classList.remove('!hidden')
    notificationHtmlElement.getElementsByClassName(
      'toast-title'
    )[0].textContent = title
    if (text && text.length) {
      let descriptionElement =
        notificationHtmlElement.getElementsByClassName('toast-description')[0]
      descriptionElement.textContent = text
      descriptionElement.classList.remove('!hidden')
    }
    notificationHtmlElement
      .getElementsByClassName(`toast-icon-${type}`)[0]
      ?.classList.remove('!hidden')

    const instance = Toastify({
      node: notificationHtmlElement,
      duration: 7000,
      newWindow: false,
      close: false,
      gravity: 'top',
      position: 'right',
      stopOnFocus: false,
      onClick: () => instance.hideToast()
    })

    instance.showToast()
  },
  toastSuccess: (title, text, uniqId) => {
    helpers.toast('success', title, text, uniqId)
  },
  toastWarning: (title, text, uniqId) => {
    helpers.toast('warning', title, text, uniqId)
  },
  toastDanger: (title, text, uniqId) => {
    helpers.toast('danger', title, text, uniqId)
  },
  toastInfo: (title, text, uniqId) => {
    helpers.toast('info', title, text, uniqId)
  },
  /**
   * FM's toast with buttons (e.g. "undo" after a delete), cloned from the
   * #toast_action_content template of ToastsContainer. `type` 'info' or
   * 'delete' shows the matching icon; each action is `{ title, action }`.
   */
  toastAction(type, title, description, actions) {
    const template = document.getElementById('toast_action_content')
    if (!template) {
      helpers.toastInfo(title, description)
      return
    }
    const toastElement = template.cloneNode(true)
    toastElement.removeAttribute('id')
    toastElement.classList.remove('hidden')
    toastElement.querySelector('.toast-title').textContent = title
    if (description) {
      toastElement.querySelector('.toast-description').textContent = description
    } else {
      toastElement.querySelector('.toast-description').remove()
    }
    if (['info', 'delete'].includes(type)) {
      toastElement
        .querySelector(`.toast-icon-${type}`)
        ?.classList.remove('!hidden')
    }
    const templateButton = toastElement.querySelector('.template-button')
    const ctaContainer = toastElement.querySelector('.toast-cta-container')
    templateButton.remove()
    ;(actions ?? []).forEach((action) => {
      const button = templateButton.cloneNode(true)
      button.classList.remove('hidden', 'template-button')
      button.innerText = action.title
      if (action.class) button.classList.add(action.class)
      if (typeof action.action === 'function') {
        button.onclick = () => {
          action.action()
          toastElement.remove()
        }
      }
      ctaContainer.append(button)
    })
    Toastify({
      node: toastElement,
      duration: 5000,
      newWindow: false,
      close: false,
      gravity: 'top',
      position: 'right',
      stopOnFocus: false
    }).showToast()
  },
  capitalizeFirstLetter(string) {
    if (string) {
      return string.charAt(0).toUpperCase() + string.slice(1)
    } else {
      return ''
    }
  },
  onlyNumber(string) {
    if (string) {
      return string.replace(/\D/g, '')
    } else {
      return ''
    }
  },
  formatCurrency(price) {
    return Number(price).toLocaleString('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    })
  },
  timeAgo(time) {
    const date = new Date((time || '').replace(/-/g, '/').replace(/[TZ]/g, ' '))
    const diff = (new Date().getTime() - date.getTime()) / 1000
    const dayDiff = Math.floor(diff / 86400)

    if (isNaN(dayDiff) || dayDiff < 0 || dayDiff >= 31) {
      return dayjs(time).format('MMMM DD, YYYY')
    }

    return (
      (dayDiff === 0 &&
        ((diff < 60 && 'just now') ||
          (diff < 120 && '1 minute ago') ||
          (diff < 3600 && Math.floor(diff / 60) + ' minutes ago') ||
          (diff < 7200 && '1 hour ago') ||
          (diff < 86400 && Math.floor(diff / 3600) + ' hours ago'))) ||
      (dayDiff === 1 && 'Yesterday') ||
      (dayDiff < 7 && dayDiff + ' days ago') ||
      (dayDiff < 31 && Math.ceil(dayDiff / 7) + ' weeks ago')
    )
  },
  diffTimeByNow(time) {
    const startDate = dayjs(dayjs().format('YYYY-MM-DD HH:mm:ss').toString())
    const endDate = dayjs(dayjs(time).format('YYYY-MM-DD HH:mm:ss').toString())

    const duration = dayjs.duration(endDate.diff(startDate))
    const milliseconds = Math.floor(duration.asMilliseconds())

    const days = Math.round(milliseconds / 86400000)
    const hours = Math.round((milliseconds % 86400000) / 3600000)
    let minutes = Math.round(((milliseconds % 86400000) % 3600000) / 60000)
    const seconds = Math.round(
      (((milliseconds % 86400000) % 3600000) % 60000) / 1000
    )

    if (seconds < 30 && seconds >= 0) {
      minutes += 1
    }

    return {
      days: days.toString().length < 2 ? '0' + days : days,
      hours: hours.toString().length < 2 ? '0' + hours : hours,
      minutes: minutes.toString().length < 2 ? '0' + minutes : minutes,
      seconds: seconds.toString().length < 2 ? '0' + seconds : seconds
    }
  },
  isset(obj) {
    if (obj !== null && obj !== undefined) {
      if (typeof obj === 'object' || Array.isArray(obj)) {
        return Object.keys(obj).length
      } else {
        return obj.toString().length
      }
    }

    return false
  },
  toRaw(obj) {
    return JSON.parse(JSON.stringify(obj))
  },
  randomNumbers(from, to, length) {
    const numbers = [0]
    for (let i = 1; i < length; i++) {
      numbers.push(Math.ceil(Math.random() * (from - to) + to))
    }

    return numbers
  }
}

const install = (app) => {
  app.config.globalProperties.$h = helpers
}

export { install as default, helpers as helper }
